/**
 * RESPONSIBILITY: request an account. Nothing else.
 *
 * Registering never grants anything. The account is created with no roles and a
 * pending status, and an administrator has to approve it — so this form being open
 * to the public costs nothing.
 */
import { useEffect, useRef, useState } from 'react';
import 'altcha';                                   // registers <altcha-widget>
import { FRAMEWORK_ROUTES } from '@framework/core';

// `altcha` ships its own <altcha-widget> JSX typing — nothing to declare here.

export function RegisterForm({ toLogin }: { toLogin: () => void }) {
  const [f, setF] = useState({ firstName: '', lastName: '', username: '', email: '', password: '' });
  const [altcha, setAltcha] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const widget = useRef<HTMLElement>(null);

  // ALTCHA solves a proof-of-work in the browser and emits the payload. Self-hosted,
  // no third-party call, nothing for the user to squint at.
  useEffect(() => {
    const el = widget.current;
    if (!el) return;
    const onState = (e: Event) => {
      const d = (e as CustomEvent<{ state: string; payload?: string }>).detail;
      setAltcha(d.state === 'verified' ? d.payload ?? null : null);
    };
    el.addEventListener('statechange', onState);
    return () => el.removeEventListener('statechange', onState);
  }, []);

  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setF({ ...f, [k]: e.target.value });

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!altcha) { setMsg({ ok: false, text: 'Please wait for the captcha to finish.' }); return; }
    setBusy(true);
    const res = await fetch(FRAMEWORK_ROUTES.register, {
      method: 'POST', headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ ...f, altcha }),
    });
    const body = await res.json();
    setBusy(false);
    setMsg({ ok: res.ok, text: body.message ?? 'Something went wrong.' });
  }

  if (msg?.ok) {
    return (
      <div className="form">
        <p className="notice-ok">{msg.text}</p>
        <p className="ctl-hint">
          Your account exists but has <strong>no roles and no access</strong> until an admin approves it.
        </p>
        <button className="primary" onClick={toLogin}>Back to sign in</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="form">
      <div className="form-row">
        <label>First name<input value={f.firstName} onChange={set('firstName')} required /></label>
        <label>Last name<input value={f.lastName} onChange={set('lastName')} required /></label>
      </div>
      <label>Username<input value={f.username} onChange={set('username')} required /></label>
      <label>Email<input type="email" value={f.email} onChange={set('email')} required /></label>
      <label>Password<input type="password" value={f.password} onChange={set('password')} required minLength={8} /></label>

      <altcha-widget ref={widget as never} challengeurl={FRAMEWORK_ROUTES.captcha} hidefooter hidelogo />

      {msg && !msg.ok && <p className="ctl-error">{msg.text}</p>}
      <button className="primary" disabled={busy}>{busy ? 'Submitting…' : 'Register'}</button>
      <p className="ctl-hint">An admin reviews every registration before it can sign in.</p>
    </form>
  );
}
